import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { FaBuilding } from 'react-icons/fa';
import './ColumbariumClient.css';
import '../components/BookingForm.css';

const ColumbariumClient = () => {
  const { token, user, updateUser } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('browse');
  const [slots, setSlots] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [buildingLayout, setBuildingLayout] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Filters for browsing
  const [filters, setFilters] = useState({
    floor: '',
    section: '',
    size: '',
    minPrice: '',
    maxPrice: '',
    page: 1
  });

  // Selected slot for reservation
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [showSlotDetailsModal, setShowSlotDetailsModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  // Reservation form
  const [reservationForm, setReservationForm] = useState({
    clientName: (user?.firstName && user?.lastName) ? `${user.firstName} ${user.lastName}` : '',
    clientContact: user?.phone || '',
    clientEmail: user?.email || '',
    deceasedName: '',
    deceasedDateOfBirth: '',
    deceasedDateOfDeath: '',
    deceasedRelationship: '',
    paymentMethod: 'gcash',
    paymentAmount: '',
    duration: 5,
    specialRequirements: '',
    proofImage: null
  });

  const [formErrors, setFormErrors] = useState({});

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^(\+63|0)[0-9]{10}$/;
    return phoneRegex.test(phone.replace(/[\s-]/g, ''));
  };

  const validateName = (name) => {
    const nameRegex = /^[a-zA-Z\s.'-]+$/;
    return name.length >= 2 && name.length <= 50 && nameRegex.test(name);
  };

  const validateDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    return date instanceof Date && !isNaN(date) && date <= today;
  };

  const validateForm = () => {
    const errors = {};

    // Client Information
    if (!reservationForm.clientName.trim()) {
      errors.clientName = 'Full name is required';
    } else if (!validateName(reservationForm.clientName)) {
      errors.clientName = 'Name must be 2-50 characters and contain only letters, spaces, and common punctuation';
    }

    if (!reservationForm.clientContact.trim()) {
      errors.clientContact = 'Contact number is required';
    } else if (!validatePhone(reservationForm.clientContact)) {
      errors.clientContact = 'Please enter a valid Philippine phone number (e.g., 09123456789)';
    }

    if (!reservationForm.clientEmail.trim()) {
      errors.clientEmail = 'Email address is required';
    } else if (!validateEmail(reservationForm.clientEmail)) {
      errors.clientEmail = 'Please enter a valid email address';
    }

    // Deceased Information
    if (!reservationForm.deceasedName.trim()) {
      errors.deceasedName = 'Deceased name is required';
    } else if (!validateName(reservationForm.deceasedName)) {
      errors.deceasedName = 'Name must be 2-50 characters and contain only letters, spaces, and common punctuation';
    }

    if (!reservationForm.deceasedDateOfBirth) {
      errors.deceasedDateOfBirth = 'Date of birth is required';
    } else if (!validateDate(reservationForm.deceasedDateOfBirth)) {
      errors.deceasedDateOfBirth = 'Please enter a valid date (not in the future)';
    }

    if (!reservationForm.deceasedDateOfDeath) {
      errors.deceasedDateOfDeath = 'Date of death is required';
    } else if (!validateDate(reservationForm.deceasedDateOfDeath)) {
      errors.deceasedDateOfDeath = 'Please enter a valid date (not in the future)';
    }

    if (!reservationForm.deceasedRelationship.trim()) {
      errors.deceasedRelationship = 'Relationship is required';
    } else if (reservationForm.deceasedRelationship.length < 2 || reservationForm.deceasedRelationship.length > 30) {
      errors.deceasedRelationship = 'Relationship must be 2-30 characters';
    }

    // Payment Information
    if (!reservationForm.proofImage) {
      errors.proofImage = 'Payment proof is required';
    }

    // Special Requirements (optional, but if provided, validate length)
    if (reservationForm.specialRequirements && reservationForm.specialRequirements.length > 500) {
      errors.specialRequirements = 'Special requirements must not exceed 500 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Parse price from string or number
  const parsePrice = (basePrice) => {
    if (typeof basePrice === 'string') {
      // Remove currency symbols, commas, and parse
      return parseFloat(basePrice.replace(/[₱,]/g, '')) || 0;
    }
    return parseInt(basePrice) || 0;
  };

  // Calculate final price based on duration
  const calculateFinalPrice = (basePrice, duration) => {
    const price = parsePrice(basePrice);
    
    switch (duration) {
      case 5:
        return Math.round(price * 1.1); // 10% increase for 5 years
      case 10:
        return Math.round(price * 1.25); // 25% increase for 10 years
      case 25:
        return Math.round(price * 1.5); // 50% increase for 25 years
      case 99:
        return Math.round(price * 2); // 100% increase for perpetual
      default:
        return Math.round(price * 1.1); // Default to 10% for 5 years
    }
  };

  // Calculate 10% down payment
  const calculateDownPayment = (basePrice) => {
    const price = parsePrice(basePrice);
    return Math.round(price * 0.1); // 10% down payment
  };

  // Auto-update payment amount when slot is selected
  useEffect(() => {
    if (selectedSlot && selectedSlot.price) {
      const downPayment = calculateDownPayment(selectedSlot.price);
      setReservationForm(prev => ({
        ...prev,
        paymentAmount: downPayment
      }));
    }
  }, [selectedSlot]);

  const api = async (endpoint, options = {}) => {
    const baseURL = window.location.hostname === 'localhost' 
      ? 'http://localhost:5000' 
      : 'https://api.grave-path.com';
    
    // Check if user is authenticated
    if (!token) {
      console.error('No authentication token available');
      return Promise.reject(new Error('User not authenticated'));
    }
    
    const response = await fetch(`${baseURL}/api${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });
    
    // Check for 401 Unauthorized - token might be expired
    if (response.status === 401) {
      console.error('Token expired or invalid, redirecting to login');
      // Clear invalid token and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(new Error('Token expired - please log in again'));
    }
    
    return response;
  };

  // Generate slots data matching the grid structure
  const generateSlotsData = (floor = 1) => {
    const slots = [];
    
    // Create a more random distribution using the same hash function as the grid
    const hash = (x, y, floorNum) => {
      let h = x * 374761393 + y * 668265263 + floorNum * 123456789;
      h = (h ^ (h >> 13)) * 1274126177;
      return (h ^ (h >> 16)) % 100;
    };

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 50; col++) { // Only data columns, no spacers
        const randomValue = hash(row, col, floor);
        let status;
        
        if (randomValue < 60) status = 'available';      // 60% available
        else if (randomValue < 85) status = 'occupied';  // 25% occupied
        else if (randomValue < 95) status = 'reserved';  // 10% reserved
        else status = 'unavailable';                     // 5% unavailable

        // Generate section based on column position
        const section = col < 10 ? 'A' : col < 20 ? 'B' : col < 30 ? 'C' : col < 40 ? 'D' : 'E';
        
        // Only include available slots for the browse page
        if (status === 'available') {
          slots.push({
            slotId: `C-F${floor}-${String(row + 1).padStart(2, '0')}-${String(col + 1).padStart(2, '0')}`,
            displayId: `M${floor}${section}${String(row + 1).padStart(2, '0')}${String(col + 1).padStart(2, '0')}`,
            building: 'Columbarium',
            floor: floor,
            section: section,
            row: row + 1,
            column: col + 1,
            size: 'Single',
            dimensions: {
              width: 30,
              height: 30,
              depth: 30
            },
            price: floor === 2 ? 40000 : 50000,
            status: status,
            location: `Floor ${floor}, Section ${section}`,
            position: `Row ${row + 1}, Column ${col + 1}`
          });
        }
      }
    }
    return slots;
  };

  const fetchAvailableSlots = async () => {
    setLoading(true);
    try {
      // For now, use the generated data instead of API call
      // This ensures consistency with the grid data
      let generatedSlots = [];
      
      if (filters.floor) {
        // Generate slots for specific floor
        generatedSlots = generateSlotsData(parseInt(filters.floor));
      } else {
        // Generate slots for all floors (1 and 2)
        generatedSlots = [...generateSlotsData(1), ...generateSlotsData(2)];
      }
      
      // Apply additional filters
      let filteredSlots = generatedSlots;
      
      if (filters.section) {
        filteredSlots = filteredSlots.filter(slot => slot.section === filters.section);
      }
      
      if (filters.size) {
        filteredSlots = filteredSlots.filter(slot => slot.size.toLowerCase() === filters.size);
      }
      
      if (filters.minPrice) {
        filteredSlots = filteredSlots.filter(slot => slot.price >= parseInt(filters.minPrice));
      }
      
      if (filters.maxPrice) {
        filteredSlots = filteredSlots.filter(slot => slot.price <= parseInt(filters.maxPrice));
      }
      
      setSlots(filteredSlots);
      
      // TODO: Replace with actual API call when backend is ready
      // const params = new URLSearchParams();
      // Object.entries(filters).forEach(([key, value]) => {
      //   if (value) params.append(key, value);
      // });
      // const response = await api(`/client/columbarium/slots?${params}`);
      // if (response.ok) {
      //   const data = await response.json();
      //   setSlots(data.slots || []);
      // } else {
      //   setError('Failed to fetch available slots');
      // }
    } catch (error) {
      setError('Error fetching slots');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyReservations = async () => {
    setLoading(true);
    try {
      const response = await api('/client/columbarium/reservations');
      if (response.ok) {
        const data = await response.json();
        setReservations(data.reservations || []);
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('Failed to fetch reservations:', response.status, errorData);
        setError(`Failed to fetch reservations: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error fetching reservations:', error);
      setError(`Error fetching reservations: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchBuildingLayout = async () => {
    try {
      const response = await api('/client/columbarium/building-layout');
      if (response.ok) {
        const data = await response.json();
        setBuildingLayout(data);
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('Failed to fetch building layout:', response.status, errorData);
        setError(`Failed to fetch building layout: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error fetching building layout:', error);
      setError(`Error fetching building layout: ${error.message}`);
    }
  };

  const handleSlotReservation = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    
    try {
      const formData = new FormData();
      
      // Add all form fields
      Object.entries(reservationForm).forEach(([key, value]) => {
        if (key === 'proofImage' && value) {
          formData.append(key, value);
        } else if (key !== 'proofImage') {
          formData.append(key, value);
        }
      });
      
      // Add slot ID
      formData.append('slotId', selectedSlot.slotId);

      const response = await api('/client/columbarium/reservations', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(data.message || 'Reservation submitted successfully!');
        setShowReservationModal(false);
        resetReservationForm();
        fetchAvailableSlots();
        fetchMyReservations();
        setCurrentStep(4);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to submit reservation');
      }
    } catch (error) {
      setError('Error submitting reservation');
    } finally {
      setLoading(false);
    }
  };

  const resetReservationForm = () => {
    setReservationForm({
      clientName: (user?.firstName && user?.lastName) ? `${user.firstName} ${user.lastName}` : '',
      clientContact: user?.phone || '',
      clientEmail: user?.email || '',
      deceasedName: '',
      deceasedDateOfBirth: '',
      deceasedDateOfDeath: '',
      deceasedRelationship: '',
      paymentMethod: 'gcash',
      paymentAmount: '',
      duration: 5,
      specialRequirements: '',
      proofImage: null
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-PH');
  };

  // Function to fetch user profile data
  const fetchUserProfile = async () => {
    try {
      const apiUrl = `${window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.grave-path.com'}/api/profile`;
      console.log('Fetching user profile from:', apiUrl);
      
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Profile response status:', response.status);
      
      if (response.ok) {
        const userData = await response.json();
        console.log('User profile data received:', userData);
        // The profile endpoint returns user data directly, not wrapped in a user property
        if (userData && updateUser) {
          updateUser(userData);
          console.log('User data updated in AuthContext');
        }
        return userData;
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('Failed to fetch user profile:', response.status, errorData);
        return null;
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };

  useEffect(() => {
    // Check if user is authenticated before making API calls
    if (!token) {
      console.error('No token available - cannot fetch data');
      setError('Please log in to access columbarium services');
      return;
    }

    // If we have a token but no user data, try to fetch it
    if (token && !user) {
      console.log('Token exists but no user data, fetching profile...');
      fetchUserProfile().then((userData) => {
        if (userData) {
          console.log('User profile fetched:', userData.email);
    fetchBuildingLayout();
        } else {
          console.error('Failed to fetch user profile');
          setError('Failed to load user data. Please log in again.');
        }
      });
      return;
    }

    if (!user) {
      console.error('No user data available');
      setError('Please log in to access columbarium services');
      return;
    }

    console.log('User authenticated:', user?.email, 'Token exists:', !!token);
    fetchBuildingLayout();
    
    // Check if user came from columbarium grid with a selected slot
    const selectedSlotData = localStorage.getItem('selectedColumbariumSlot');
    const reservationStep = localStorage.getItem('columbariumReservationStep');
    
    if (selectedSlotData && reservationStep === '3') {
      // User came from grid, skip to step 3 with selected slot
      const slotData = JSON.parse(selectedSlotData);
      setSelectedSlot(slotData);
      setReservationForm({
        ...reservationForm,
        paymentAmount: calculateDownPayment(slotData.price) // 10% down payment
      });
      setShowReservationModal(true);
      setCurrentStep(3);
      
      // Clear the localStorage data
      localStorage.removeItem('selectedColumbariumSlot');
      localStorage.removeItem('columbariumReservationStep');
    } else if (activeTab === 'browse') {
      fetchAvailableSlots();
      setCurrentStep(1);
    } else if (activeTab === 'reservations') {
      fetchMyReservations();
      setCurrentStep(5);
    } else if (activeTab === 'layout') {
      setCurrentStep(1);
    }
  }, [activeTab, filters, token, user]);

  // Update form when user data becomes available
  useEffect(() => {
    if (user && (user.firstName || user.lastName)) {
      setReservationForm(prev => ({
        ...prev,
        clientName: (user.firstName && user.lastName) ? `${user.firstName} ${user.lastName}` : prev.clientName,
        clientContact: user.phone || prev.clientContact,
        clientEmail: user.email || prev.clientEmail
      }));
    }
  }, [user]);

  // Update step when user selects a slot
  const handleSlotSelection = (slot) => {
    setSelectedSlot(slot);
    setCurrentStep(2);
  };

  // Update step when user opens reservation modal
  const handleReservationStart = (slot) => {
    setSelectedSlot(slot);
    setReservationForm({
      ...reservationForm,
      paymentAmount: slot.price
    });
    setShowReservationModal(true);
    setCurrentStep(3);
  };

  return (
    <div className="columbarium-client">
      <div className="bookmark-header">
        <h1>
          <FaBuilding className="header-icon" />
          Columbarium Services
        </h1>
        <p className="header-subtitle">
          Browse and reserve columbarium slots for your loved ones
        </p>
      </div>

      {/* Process Flow Section */}
      <div className="process-section">
        <div className="section-header">
          <h2>How It Works</h2>
        </div>
        
        <div className="process-flow">
          <div className={`process-step ${currentStep >= 1 ? 'active' : ''}`}>
            <div className="step-number">1</div>
            <div className="step-content">
              <h3>Browse & Filter</h3>
              <p>Find available slots by location, size, and price</p>
            </div>
          </div>

          <div className="process-arrow">→</div>

          <div className={`process-step ${currentStep >= 2 ? 'active' : ''}`}>
            <div className="step-number">2</div>
            <div className="step-content">
              <h3>Select Slot</h3>
              <p>Choose your preferred slot and click reserve</p>
            </div>
          </div>

          <div className="process-arrow">→</div>

          <div className={`process-step ${currentStep >= 3 ? 'active' : ''}`}>
            <div className="step-number">3</div>
            <div className="step-content">
              <h3>Complete Form</h3>
              <p>Provide details and upload payment proof</p>
            </div>
          </div>

          <div className="process-arrow">→</div>

          <div className={`process-step ${currentStep >= 4 ? 'active' : ''}`}>
            <div className="step-number">4</div>
            <div className="step-content">
              <h3>Submit & Wait</h3>
              <p>Staff reviews your application (1-3 days)</p>
            </div>
          </div>

          <div className="process-arrow">→</div>

          <div className={`process-step ${currentStep >= 5 ? 'active' : ''}`}>
            <div className="step-number">5</div>
            <div className="step-content">
              <h3>Get Confirmed</h3>
              <p>Receive approval and manage your reservation</p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
          <button onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={activeTab === 'browse' ? 'active' : ''}
          onClick={() => setActiveTab('browse')}
        >
          Browse Slots
        </button>
        <button 
          className={activeTab === 'reservations' ? 'active' : ''}
          onClick={() => setActiveTab('reservations')}
        >
          My Reservations
        </button>
        <button 
          className={activeTab === 'layout' ? 'active' : ''}
          onClick={() => setActiveTab('layout')}
        >
          Building Layout
        </button>
      </div>

      {/* Browse Slots Tab */}
      {activeTab === 'browse' && (
        <div className="browse-section">
          <div className="section-header">
            <h2>Available Columbarium Slots</h2>
          </div>

          {/* Filters */}
          <div className="filters">
            <select 
              value={filters.floor}
              onChange={(e) => setFilters({...filters, floor: e.target.value, page: 1})}
            >
              <option value="">All Floors</option>
              {[1,2].map(floor => (
                <option key={floor} value={floor}>Floor {floor}</option>
              ))}
            </select>

            <select 
              value={filters.section}
              onChange={(e) => setFilters({...filters, section: e.target.value, page: 1})}
            >
              <option value="">All Sections</option>
              {['A','B','C','D','E','F'].map(section => (
                <option key={section} value={section}>Section {section}</option>
              ))}
            </select>

            <select 
              value={filters.size}
              onChange={(e) => setFilters({...filters, size: e.target.value, page: 1})}
            >
              <option value="">All Sizes</option>
              <option value="single">Single</option>
              <option value="double">Double</option>
              <option value="family">Family</option>
            </select>

            <input 
              type="number"
              placeholder="Min Price"
              value={filters.minPrice}
              onChange={(e) => setFilters({...filters, minPrice: e.target.value, page: 1})}
            />

            <input 
              type="number"
              placeholder="Max Price"
              value={filters.maxPrice}
              onChange={(e) => setFilters({...filters, maxPrice: e.target.value, page: 1})}
            />
          </div>

          {/* Slots Grid */}
          <div className="slots-grid">
            {loading ? (
              <div className="loading">Loading available slots...</div>
            ) : slots.length === 0 ? (
              <div className="no-data">No slots available matching your criteria</div>
            ) : (
              slots.map(slot => (
                <div key={slot.slotId} className="slot-card">
                  <div className="slot-header">
                    <h3 className="slot-id">{slot.slotId}</h3>
                    <span className={`size-badge ${slot.size}`}>{slot.size}</span>
                  </div>
                  
                  <div className="slot-details">
                    <div className="detail-item">
                      <span className="label">Location:</span>
                      <span>Floor {slot.floor}, Section {slot.section}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Position:</span>
                      <span>Row {slot.row}, Column {slot.column}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Dimensions:</span>
                      <span>{slot.dimensions.width}×{slot.dimensions.height}×{slot.dimensions.depth} cm</span>
                    </div>
                  </div>
                  
                  <div className="slot-price">
                    {formatCurrency(slot.price)}
                  </div>
                  
                  <div className="slot-actions">
                    <button 
                      className="btn btn-primary btn-sm"
                      onClick={() => handleReservationStart(slot)}
                    >
                      Reserve Now
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* My Reservations Tab */}
      {activeTab === 'reservations' && (
        <div className="reservations-section">
          <div className="section-header">
            <h2>My Columbarium Reservations</h2>
          </div>

          <div className="reservations-list">
            {loading ? (
              <div className="loading">Loading your reservations...</div>
            ) : reservations.length === 0 ? (
              <div className="no-data">You have no columbarium reservations yet</div>
            ) : (
              reservations.map(reservation => (
                <div key={reservation._id} className="reservation-card">
                  <div className="reservation-header">
                    <h3>Reservation #{reservation._id.slice(-6)}</h3>
                    <span className={`status-badge ${reservation.status}`}>
                      {reservation.status.toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="reservation-details">
                    <div className="details-grid">
                      <div className="detail-group">
                        <h4>Slot Information</h4>
                        <p><strong>Slot ID:</strong> {reservation.slotId}</p>
                        {reservation.slotDetails && (
                          <>
                            <p><strong>Location:</strong> Floor {reservation.slotDetails.floor}, Section {reservation.slotDetails.section}</p>
                            <p><strong>Size:</strong> {reservation.slotDetails.size}</p>
                            <p><strong>Price:</strong> {formatCurrency(reservation.slotDetails.price)}</p>
                          </>
                        )}
                      </div>
                      
                      <div className="detail-group">
                        <h4>Deceased Information</h4>
                        <p><strong>Name:</strong> {reservation.deceasedInfo.name}</p>
                        <p><strong>Relationship:</strong> {reservation.deceasedInfo.relationship}</p>
                        <p><strong>Birth:</strong> {formatDate(reservation.deceasedInfo.dateOfBirth)}</p>
                        <p><strong>Death:</strong> {formatDate(reservation.deceasedInfo.dateOfDeath)}</p>
                      </div>
                      
                      <div className="detail-group">
                        <h4>Payment Information</h4>
                        <p><strong>Method:</strong> {reservation.paymentMethod.toUpperCase()}</p>
                        <p><strong>Amount:</strong> {formatCurrency(reservation.paymentAmount)}</p>
                        <p><strong>Duration:</strong> {reservation.duration} years</p>
                      </div>
                      
                      <div className="detail-group">
                        <h4>Status Information</h4>
                        <p><strong>Submitted:</strong> {formatDate(reservation.createdAt)}</p>
                        {reservation.approvedAt && (
                          <p><strong>Approved:</strong> {formatDate(reservation.approvedAt)}</p>
                        )}
                        {reservation.renewalDate && (
                          <p><strong>Renewal Due:</strong> {formatDate(reservation.renewalDate)}</p>
                        )}
                      </div>
                    </div>
                    
                    {reservation.specialRequirements && (
                      <div className="special-requirements">
                        <h4>Special Requirements</h4>
                        <p>{reservation.specialRequirements}</p>
                      </div>
                    )}
                    
                    {reservation.rejectionReason && (
                      <div className="rejection-reason">
                        <h4>Rejection Reason</h4>
                        <p>{reservation.rejectionReason}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Building Layout Tab */}
      {activeTab === 'layout' && (
        <div className="layout-section">
          <div className="section-header">
            <h2>Columbarium Building Layout</h2>
          </div>
          
          {buildingLayout ? (
            <div className="building-layout">
              <div className="layout-info">
                <p>Total Available Slots: <strong>{buildingLayout.metadata.totalAvailable}</strong></p>
                <p>Floors: <strong>{buildingLayout.metadata.floors.join(', ')}</strong></p>
                <p>Sections: <strong>{buildingLayout.metadata.sections.join(', ')}</strong></p>
              </div>
              
              <div className="floors-container">
                {buildingLayout.metadata.floors.map(floor => (
                  <div key={floor} className="floor-layout">
                    <h3>Floor {floor}</h3>
                    <div className="sections-grid">
                      {buildingLayout.metadata.sections.map(section => {
                        const sectionSlots = buildingLayout.layout[floor]?.[section] || [];
                        return (
                          <div key={section} className="section-block">
                            <h4>Section {section}</h4>
                            <div className="slots-count">
                              {sectionSlots.length} available
                            </div>
                            <div className="slots-preview">
                              {sectionSlots.slice(0, 6).map(slot => (
                                <div key={slot.slotId} className="slot-mini">
                                  {slot.slotId}
                                </div>
                              ))}
                              {sectionSlots.length > 6 && (
                                <div className="slot-mini more">
                                  +{sectionSlots.length - 6}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="loading">Loading building layout...</div>
          )}
        </div>
      )}

      {/* Slot Details Modal */}
      {showSlotDetailsModal && selectedSlot && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Slot Details - {selectedSlot.slotId}</h3>
              <button onClick={() => setShowSlotDetailsModal(false)}>×</button>
            </div>
            <div className="slot-details-modal">
              <div className="details-grid">
                <div className="detail-item">
                  <span className="label">Building:</span>
                  <span>{selectedSlot.building}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Floor:</span>
                  <span>{selectedSlot.floor}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Section:</span>
                  <span>{selectedSlot.section}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Row:</span>
                  <span>{selectedSlot.row}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Column:</span>
                  <span>{selectedSlot.column}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Size:</span>
                  <span className={`size-badge ${selectedSlot.size}`}>{selectedSlot.size}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Dimensions:</span>
                  <span>{selectedSlot.dimensions.width} × {selectedSlot.dimensions.height} × {selectedSlot.dimensions.depth} cm</span>
                </div>
                <div className="detail-item">
                  <span className="label">Price:</span>
                  <span className="price">{formatCurrency(selectedSlot.price)}</span>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowSlotDetailsModal(false)}>Close</button>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  setShowSlotDetailsModal(false);
                  setReservationForm({
                    ...reservationForm,
                    paymentAmount: selectedSlot.price
                  });
                  setShowReservationModal(true);
                }}
              >
                Reserve This Slot
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reservation Modal */}
      {showReservationModal && selectedSlot && (
        <div className="booking-modal-overlay">
          <div className="booking-modal-content" style={{ 
            maxWidth: '800px', 
            textAlign: 'center',
            maxHeight: '90vh',
            overflowY: 'auto',
            padding: '2rem'
          }}>
            <div className="booking-modal-icon">🏛️</div>
            <h3>Reserve Slot {selectedSlot.displayId || selectedSlot.slotId}</h3>
            <p>Complete the form below to reserve this columbarium slot</p>
            
            <form onSubmit={handleSlotReservation}>
              <div className="form-sections" style={{ marginTop: '1.5rem', textAlign: 'left' }}>
                <div className="form-section">
                  <h4 style={{ margin: '0 0 0.75rem 0', color: '#333', fontSize: '1rem', fontWeight: '600', textAlign: 'left' }}>Client Information</h4>
                  <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div className="field-group">
                      <input 
                        type="text"
                      placeholder=" "
                        value={reservationForm.clientName}
                      onChange={(e) => {
                        const value = e.target.value.slice(0, 50); // Limit to 50 characters
                        setReservationForm({...reservationForm, clientName: value});
                        // Clear error when user starts typing
                        if (formErrors.clientName) {
                          setFormErrors({...formErrors, clientName: ''});
                        }
                      }}
                        required
                      maxLength={50}
                      />
                    <label>Full Name</label>
                    {formErrors.clientName && (
                      <div style={{ color: '#dc2626', fontSize: '11px', marginTop: '4px' }}>
                        {formErrors.clientName}
                    </div>
                    )}
                  </div>
                    <div className="field-group">
                      <input 
                        type="tel"
                        placeholder=" "
                        value={reservationForm.clientContact}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9+\-\s]/g, '').slice(0, 15); // Only allow numbers, +, -, space
                          setReservationForm({...reservationForm, clientContact: value});
                          if (formErrors.clientContact) {
                            setFormErrors({...formErrors, clientContact: ''});
                          }
                        }}
                        required
                        maxLength={15}
                      />
                      <label>Contact Number</label>
                      {formErrors.clientContact && (
                        <div style={{ color: '#dc2626', fontSize: '11px', marginTop: '4px' }}>
                          {formErrors.clientContact}
                    </div>
                      )}
                    </div>
                    <div className="field-group" style={{ gridColumn: '1 / -1' }}>
                      <input 
                        type="email"
                        placeholder=" "
                        value={reservationForm.clientEmail}
                        onChange={(e) => {
                          const value = e.target.value.slice(0, 100); // Limit to 100 characters
                          setReservationForm({...reservationForm, clientEmail: value});
                          if (formErrors.clientEmail) {
                            setFormErrors({...formErrors, clientEmail: ''});
                          }
                        }}
                        required
                        maxLength={100}
                      />
                      <label>Email Address</label>
                      {formErrors.clientEmail && (
                        <div style={{ color: '#dc2626', fontSize: '11px', marginTop: '4px' }}>
                          {formErrors.clientEmail}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h4 style={{ margin: '0 0 0.75rem 0', color: '#333', fontSize: '1rem', fontWeight: '600', textAlign: 'left' }}>Deceased Information</h4>
                  <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                    <div className="field-group" style={{ gridColumn: '1 / -1' }}>
                      <input 
                        type="text"
                        placeholder=" "
                        value={reservationForm.deceasedName}
                        onChange={(e) => {
                          const value = e.target.value.slice(0, 50); // Limit to 50 characters
                          setReservationForm({...reservationForm, deceasedName: value});
                          if (formErrors.deceasedName) {
                            setFormErrors({...formErrors, deceasedName: ''});
                          }
                        }}
                        required
                        maxLength={50}
                      />
                      <label>Full Name of Deceased</label>
                      {formErrors.deceasedName && (
                        <div style={{ color: '#dc2626', fontSize: '11px', marginTop: '4px' }}>
                          {formErrors.deceasedName}
                    </div>
                      )}
                    </div>
                    <div className="field-group">
                      <input 
                        type="date"
                        value={reservationForm.deceasedDateOfBirth}
                        onChange={(e) => {
                          setReservationForm({...reservationForm, deceasedDateOfBirth: e.target.value});
                          if (formErrors.deceasedDateOfBirth) {
                            setFormErrors({...formErrors, deceasedDateOfBirth: ''});
                          }
                        }}
                        required
                        max={new Date().toISOString().split('T')[0]} // Cannot be in the future
                      />
                      <label>Date of Birth</label>
                      {formErrors.deceasedDateOfBirth && (
                        <div style={{ color: '#dc2626', fontSize: '11px', marginTop: '4px' }}>
                          {formErrors.deceasedDateOfBirth}
                    </div>
                      )}
                    </div>
                    <div className="field-group">
                      <input 
                        type="date"
                        value={reservationForm.deceasedDateOfDeath}
                        onChange={(e) => {
                          setReservationForm({...reservationForm, deceasedDateOfDeath: e.target.value});
                          if (formErrors.deceasedDateOfDeath) {
                            setFormErrors({...formErrors, deceasedDateOfDeath: ''});
                          }
                        }}
                        required
                        max={new Date().toISOString().split('T')[0]} // Cannot be in the future
                      />
                      <label>Date of Death</label>
                      {formErrors.deceasedDateOfDeath && (
                        <div style={{ color: '#dc2626', fontSize: '11px', marginTop: '4px' }}>
                          {formErrors.deceasedDateOfDeath}
                    </div>
                      )}
                    </div>
                    <div className="field-group" style={{ gridColumn: '1 / -1' }}>
                      <input 
                        type="text"
                        placeholder=" "
                        value={reservationForm.deceasedRelationship}
                        onChange={(e) => {
                          const value = e.target.value.slice(0, 30); // Limit to 30 characters
                          setReservationForm({...reservationForm, deceasedRelationship: value});
                          if (formErrors.deceasedRelationship) {
                            setFormErrors({...formErrors, deceasedRelationship: ''});
                          }
                        }}
                        required
                        maxLength={30}
                      />
                      <label>Relationship to You</label>
                      {formErrors.deceasedRelationship && (
                        <div style={{ color: '#dc2626', fontSize: '11px', marginTop: '4px' }}>
                          {formErrors.deceasedRelationship}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h4 style={{ margin: '0 0 0.75rem 0', color: '#333', fontSize: '1rem', fontWeight: '600', textAlign: 'left' }}>Payment Information</h4>
                  <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                    <div className="form-field-group">
                      <label style={{ display: 'block', marginBottom: '4px', color: '#1d1d1f', fontWeight: '500', fontSize: '13px' }}>Payment Method</label>
                      <select 
                        value={reservationForm.paymentMethod}
                        onChange={(e) => setReservationForm({...reservationForm, paymentMethod: e.target.value})}
                        required
                        style={{
                          width: '100%',
                          padding: '10px',
                          fontSize: '12px',
                          color: '#333',
                          background: '#fafafa',
                          border: '1px solid #ccc',
                          borderRadius: '5px',
                          transition: 'border-color 0.2s, box-shadow 0.2s'
                        }}
                      >
                        <option value="gcash">GCash</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="bank_details">Bank Details</option>
                        <option value="credit_card">Credit Card</option>
                      </select>
                      {(reservationForm.paymentMethod === 'gcash' || reservationForm.paymentMethod === 'bank_transfer' || reservationForm.paymentMethod === 'bank_details') && (
                        <button
                          type="button"
                          onClick={() => setShowQRModal(true)}
                          style={{
                            marginTop: '4px',
                            padding: '0',
                            background: 'none',
                            border: 'none',
                            color: '#6c757d',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontStyle: 'italic',
                            textDecoration: 'underline',
                            display: 'inline-block',
                            textAlign: 'left',
                            width: 'auto',
                            marginLeft: '0'
                          }}
                        >
                          Show details
                        </button>
                      )}
                    </div>
                    <div className="form-field-group">
                      <label style={{ display: 'block', marginBottom: '4px', color: '#1d1d1f', fontWeight: '500', fontSize: '13px' }}>Payment Amount (PHP)</label>
                      <input 
                        type="number"
                        value={reservationForm.paymentAmount}
                        readOnly
                        style={{
                          width: '100%',
                          padding: '10px',
                          fontSize: '12px',
                          color: '#333',
                          background: '#f5f5f5',
                          border: '1px solid #ccc',
                          borderRadius: '5px',
                          cursor: 'not-allowed'
                        }}
                      />
                      <div style={{ fontSize: '10px', color: '#666', marginTop: '4px', lineHeight: '1.3' }}>
                        <div>Base Price: {formatCurrency(parsePrice(selectedSlot.price || 0))}</div>
                        <div>Duration: {reservationForm.duration === 99 ? 'Perpetual' : `${reservationForm.duration} years`}</div>
                        <div style={{ fontWeight: '600', color: '#333' }}>
                          Final Price: {formatCurrency(calculateFinalPrice(selectedSlot.price || 0, reservationForm.duration))}
                    </div>
                        <div style={{ fontWeight: '600', color: '#2563eb', marginTop: '2px' }}>
                          Down Payment (10%): {formatCurrency(calculateDownPayment(selectedSlot.price || 0))}
                        </div>
                      </div>
                    </div>
                    <div className="form-field-group">
                      <label style={{ display: 'block', marginBottom: '4px', color: '#1d1d1f', fontWeight: '500', fontSize: '13px' }}>Duration (Years)</label>
                      <select 
                        value={reservationForm.duration}
                        onChange={(e) => setReservationForm({...reservationForm, duration: parseInt(e.target.value)})}
                        required
                        style={{
                          width: '100%',
                          padding: '10px',
                          fontSize: '12px',
                          color: '#333',
                          background: '#fafafa',
                          border: '1px solid #ccc',
                          borderRadius: '5px',
                          transition: 'border-color 0.2s, box-shadow 0.2s'
                        }}
                      >
                        <option value={5}>5 years</option>
                        <option value={10}>10 years</option>
                        <option value={25}>25 years</option>
                        <option value={99}>Perpetual</option>
                      </select>
                    </div>
                    <div className="form-field-group">
                      <label style={{ display: 'block', marginBottom: '4px', color: '#1d1d1f', fontWeight: '500', fontSize: '13px' }}>Payment Proof (Required)</label>
                      <input 
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          // Validate file size (max 5MB)
                          if (file && file.size > 5 * 1024 * 1024) {
                            setFormErrors({...formErrors, proofImage: 'File size must not exceed 5MB'});
                            return;
                          }
                          // Validate file type
                          const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
                          if (file && !allowedTypes.includes(file.type)) {
                            setFormErrors({...formErrors, proofImage: 'Please upload a valid image (JPEG, PNG, GIF) or PDF file'});
                            return;
                          }
                          setReservationForm({...reservationForm, proofImage: file});
                          if (formErrors.proofImage) {
                            setFormErrors({...formErrors, proofImage: ''});
                          }
                        }}
                        required
                        style={{
                          width: '100%',
                          padding: '10px',
                          fontSize: '12px',
                          color: '#333',
                          background: '#fafafa',
                          border: '1px solid #ccc',
                          borderRadius: '5px',
                          transition: 'border-color 0.2s, box-shadow 0.2s'
                        }}
                      />
                      <small style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
                        Upload receipt or proof of payment (Max 5MB, Images or PDF only)
                      </small>
                      {formErrors.proofImage && (
                        <div style={{ color: '#dc2626', fontSize: '11px', marginTop: '4px' }}>
                          {formErrors.proofImage}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h4 style={{ margin: '0 0 0.75rem 0', color: '#333', fontSize: '1rem', fontWeight: '600', textAlign: 'left' }}>Additional Information</h4>
                  <div className="form-field-group">
                    <label style={{ display: 'block', marginBottom: '4px', color: '#1d1d1f', fontWeight: '500', fontSize: '13px' }}>Special Requirements (Optional)</label>
                    <textarea 
                      value={reservationForm.specialRequirements}
                      onChange={(e) => {
                        const value = e.target.value.slice(0, 500); // Limit to 500 characters
                        setReservationForm({...reservationForm, specialRequirements: value});
                        if (formErrors.specialRequirements) {
                          setFormErrors({...formErrors, specialRequirements: ''});
                        }
                      }}
                      placeholder="Any special requirements or requests..."
                      rows="3"
                      maxLength={500}
                      style={{
                        width: '100%',
                        padding: '10px',
                        fontSize: '12px',
                        color: '#333',
                        background: '#fafafa',
                        border: '1px solid #ccc',
                        borderRadius: '5px',
                        transition: 'border-color 0.2s, box-shadow 0.2s',
                        resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                    />
                    <div style={{ fontSize: '10px', color: '#666', marginTop: '4px', textAlign: 'right' }}>
                      {reservationForm.specialRequirements.length}/500 characters
                  </div>
                    {formErrors.specialRequirements && (
                      <div style={{ color: '#dc2626', fontSize: '11px', marginTop: '4px' }}>
                        {formErrors.specialRequirements}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="booking-modal-actions" style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                <button 
                  type="button" 
                  onClick={() => setShowReservationModal(false)}
                  className="booking-btn-cancel"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={loading}
                  className="booking-btn-submit"
                >
                  {loading ? (
                    <>
                      <span className="booking-spinner"></span>
                      Submitting...
                    </>
                  ) : (
                    'Submit Reservation'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Details Modal */}
      {showQRModal && (
        <div className="booking-modal-overlay" onClick={() => setShowQRModal(false)}>
          <div className="booking-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px', padding: '1.5rem' }}>
            <div style={{ marginBottom: '15px' }}>
              <h3 style={{ margin: 0, fontSize: '1.2rem' }}>
                {reservationForm.paymentMethod === 'gcash' ? 'GCash Payment Details' : 
                 reservationForm.paymentMethod === 'bank_transfer' ? 'Bank Transfer Payment Details' : 
                 'Bank Account Details'}
              </h3>
            </div>
            
            {reservationForm.paymentMethod === 'bank_details' ? (
              <div id="bank-details-print">
                <div style={{ background: '#f8f9fa', padding: '1.2rem', borderRadius: '8px', marginBottom: '12px' }}>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '11px', color: '#666', marginBottom: '3px', fontWeight: '600' }}>Bank Name</label>
                    <p style={{ margin: 0, fontSize: '15px', fontWeight: '500', color: '#333' }}>Philippine National Bank (PNB)</p>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '11px', color: '#666', marginBottom: '3px', fontWeight: '600' }}>Account Name</label>
                    <p style={{ margin: 0, fontSize: '15px', fontWeight: '500', color: '#333' }}>GravePath Memorial Services</p>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '11px', color: '#666', marginBottom: '3px', fontWeight: '600' }}>Account Number</label>
                    <p style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#007bff', letterSpacing: '1px' }}>1234-5678-9012-3456</p>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '11px', color: '#666', marginBottom: '3px', fontWeight: '600' }}>Branch</label>
                    <p style={{ margin: 0, fontSize: '15px', fontWeight: '500', color: '#333' }}>Manila Main Branch</p>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', color: '#666', marginBottom: '3px', fontWeight: '600' }}>SWIFT Code</label>
                    <p style={{ margin: 0, fontSize: '15px', fontWeight: '500', color: '#333' }}>PNBMPHMM</p>
                  </div>
                </div>
                
                <div style={{ background: '#fff3cd', padding: '10px', borderRadius: '8px', marginBottom: '12px', border: '1px solid #ffc107' }}>
                  <p style={{ margin: 0, fontSize: '12px', color: '#856404', lineHeight: '1.5' }}>
                    <strong>Important Note:</strong> Please write down these details, take a picture, or screenshot this information for your records. 
                    You can also use the print button below to get a hard copy.
                  </p>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <img
                  src={reservationForm.paymentMethod === 'gcash' ? '/GCashQR.jpg' : '/BankTransferQR.jpg'}
                  alt={reservationForm.paymentMethod === 'gcash' ? 'GCash QR Code' : 'Bank Transfer QR Code'}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '380px',
                    height: 'auto',
                    width: 'auto',
                    borderRadius: '8px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                  }}
                />
                <p style={{ marginTop: '12px', marginBottom: '12px', fontSize: '14px', color: '#666' }}>
                  Scan this QR code to complete your payment
                </p>
              </div>
            )}
            
            <div className="booking-modal-actions" style={{ marginTop: '5px', display: 'flex', gap: '8px', justifyContent: 'center' }}>
              {reservationForm.paymentMethod === 'bank_details' && (
                <button
                  onClick={() => window.print()}
                  className="booking-btn-modal"
                  style={{ background: 'transparent', borderColor: '#28a745', color: '#28a745' }}
                >
                  Print
                </button>
              )}
              <button
                onClick={() => setShowQRModal(false)}
                className="booking-btn-modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColumbariumClient;
